import { IClientUserWithAccess } from '@lovoldsystem/authorization.client/components/users/authorizationMapping.action'
import { FacilityTypes } from 'src/enums'

type UserScopeSortRow = {
  clientUserMappings?: (IClientUserWithAccess & { mappingId?: number | null })[]
  scopes?: { name?: string; type?: number }[]
}

function formatScopeNameForSort(name: string, translate: (key: string, options?: { ns: string }) => string) {
  if (!name) return ''
  return `${name}`.substring(0, 3) == 'all' ? translate(name, { ns: 'common' }) : name
}

export function getScopeColumnSortValue(
  row: UserScopeSortRow,
  translate: (key: string, options?: { ns: string }) => string,
) {
  const clientAdmin = row.clientUserMappings?.find(
    c => c.type == FacilityTypes.client && (c.mappingId == 0 || c.mappingId == null),
  )
  if (clientAdmin != null) {
    return translate('full-access-to-client', { ns: 'access-control' })
  }

  const scopes = row.scopes ?? []
  if (scopes.length === 0) {
    return ''
  }

  const localityScopes = scopes.filter(
    s => s.type === FacilityTypes.locality || s.type === FacilityTypes.localityGroup,
  )
  const serviceStationScopes = scopes.filter(
    s => s.type === FacilityTypes.serviceStation || s.type === FacilityTypes.serviceStationGroup,
  )
  const storageScopes = scopes.filter(s => s.type === FacilityTypes.storage)

  const parts: string[] = []
  for (const group of [localityScopes, serviceStationScopes, storageScopes]) {
    const labels = group
      .map(s => formatScopeNameForSort(s.name ?? '', translate))
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
    if (labels.length) {
      parts.push(labels.join('|'))
    }
  }
  return parts.join(' ')
}

export function getCustomerUserUniqueRoleMappings(row: { clientUserMappings?: IClientUserWithAccess[] }) {
  return (
    row.clientUserMappings
      ?.filter(c => c.roleId != null)
      .filter((value, index, self) => self.findIndex(v => v.roleId === value.roleId) === index) ?? []
  )
}

export function getValidUntilColumnSortValue(row: { clientUserMappings?: { validUntil?: string | null }[] }) {
  const validUntilDates = row.clientUserMappings?.map(m => m.validUntil).filter((date): date is string => date != null) ?? []
  if (validUntilDates.length === 0 || !validUntilDates.every(date => date === validUntilDates[0])) {
    return null
  }
  const timestamp = new Date(validUntilDates[0]).getTime()
  return Number.isNaN(timestamp) ? null : timestamp
}

export function getRolesColumnSortValue(
  row: { clientUserMappings?: IClientUserWithAccess[] },
  translate: (key: string, options?: { ns: string }) => string,
) {
  const roleBasedMappings = getCustomerUserUniqueRoleMappings(row)
  const directPermissionsMappings = (row.clientUserMappings ?? []).filter(
    ul => ul.permissions != null && ul.permissions.length > 0,
  )
  if (roleBasedMappings.length === 0 && directPermissionsMappings.length === 0) {
    return translate('no-client-roles', { ns: 'access-control' })
  }
  const names = roleBasedMappings
    .map(m => translate(m.roleName || '', { ns: 'access-control' }))
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
  const directSuffix =
    directPermissionsMappings.length > 0 ? translate('plusDirectPermissions', { ns: 'access-control' }) : ''
  return [...names, directSuffix].filter(Boolean).join(' ')
}
